import SidebarAdmin from "./sidebar-admin";
import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";

interface VisaPack {
  id: number;
  name_en?: string;
  name_ar?: string;
  name?: string;
}

interface VisaApplication {
  id: number;
  reference_number: string;
  firstname: string;
  lastname: string;
  email: string;
  passport_no: string;
  created_at: string;
  status: 'pending' | 'approved' | 'rejected' | 'in_review' | 'completed' | string;
  visa_pack?: VisaPack;
}

interface PaginatedResponse {
  current_page: number;
  data: VisaApplication[];
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

const ListRequestVisa = () => {
  const [applications, setApplications] = useState<VisaApplication[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [fromItem, setFromItem] = useState<number>(0);
  const [toItem, setToItem] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedApplication, setSelectedApplication] = useState<VisaApplication | null>(null);

  const navigate = useNavigate();

  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // Récupération des demandes depuis l'API Laravel paginée
  const fetchApplications = useCallback(async (page = 1, search = "", status = "all") => {
    try {
      setIsLoading(true);
      const params: Record<string, any> = { page };

      if (search.trim() !== "") {
        params.search = search.trim();
      }

      if (status !== "all") {
        params.status = status;
      }

      const response = await axios.get<PaginatedResponse>(`${API_URL}/visa-applications`, { params, ...config_token } );
      const resData = response.data;

      setApplications(resData.data || []);
      setCurrentPage(resData.current_page || 1);
      setLastPage(resData.last_page || 1);
      setTotalItems(resData.total || 0);
      setFromItem(resData.from || 0);
      setToItem(resData.to || 0);
    } catch (error) {
      console.error("Error fetching visa applications:", error);
      toast.error("Error loading visa applications");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Déclencheur du fetch lors des changements de page/filtres
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchApplications(currentPage, searchTerm, statusFilter);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, statusFilter, fetchApplications]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  const handleDeleteClick = (app: VisaApplication) => {
    setSelectedApplication(app);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedApplication) return;

    try {
      await axios.delete(`${API_URL}/visa-applications/${selectedApplication.id}`, config_token);
      toast.success("Visa application deleted successfully");
      fetchApplications(currentPage, searchTerm, statusFilter);
    } catch (error) {
      console.error("Error deleting visa application:", error);
      toast.error("Failed to delete visa application");
    } finally {
      setShowDeleteModal(false);
      setSelectedApplication(null);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "--";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatStatus = (status: string) => {
    if (!status) return "";
    return status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case "pending":
        return { bg: "#fefce8", color: "#a16207", border: "#fef08a" };
      case "approved":
        return { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
      case "rejected":
        return { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
      case "in_review":
        return { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
      default:
        return { bg: "#f8fafc", color: "#475569", border: "#e2e8f0" };
    }
  };

  const getPaginationRange = (current: number, total: number) => {
    const delta = 1;
    const range: (number | string)[] = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }
    return range;
  };

  function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Header Skeleton */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <div style={{ 
              width: 220, 
              height: 28,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
              backgroundSize: "200% 100%",
              borderRadius: 6,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{ 
              width: 60, 
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
              backgroundSize: "200% 100%",
              borderRadius: 12,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{ 
            width: 280, 
            height: 18,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>
      </div>

      {/* Main Card Skeleton */}
      <div style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        marginTop: "15px",
      }}>
        {/* Filter Bar Skeleton */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7" }}>
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-7 col-lg-6">
              <div style={{ 
                width: "100%", 
                height: 42,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
            <div className="col-12 col-md-5 col-lg-4">
              <div style={{ 
                width: "100%", 
                height: 42,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          </div>
        </div>

        {/* Table Header Skeleton */}
        <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <div className="row">
            {["Reference", "Applicant", "Visa Pack", "Passport No", "Date", "Status", "Actions"].map((_, i) => (
              <div key={i} className="col" style={{ padding: "0 8px" }}>
                <div style={{ 
                  width: i === 0 ? 70 : i === 1 ? 80 : i === 2 ? 80 : i === 3 ? 80 : i === 4 ? 50 : i === 5 ? 60 : 50,
                  height: 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Table Rows Skeleton */}
        {[...Array(8)].map((_, rowIndex) => (
          <div 
            key={rowIndex} 
            style={{ 
              padding: "14px 16px", 
              borderBottom: rowIndex < 7 ? "1px solid #f1f5f9" : "none",
              display: "flex",
              alignItems: "center"
            }}
          >
            {[...Array(7)].map((_, colIndex) => (
              <div key={colIndex} style={{ flex: 1, padding: "0 8px" }}>
                <div style={{ 
                  width: colIndex === 0 ? 100 : colIndex === 1 ? 140 : colIndex === 2 ? 100 : colIndex === 3 ? 90 : colIndex === 4 ? 80 : colIndex === 5 ? 80 : 70,
                  height: colIndex === 1 ? 16 : 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                {colIndex === 1 && (
                  <div style={{ 
                    width: 120, 
                    height: 12,
                    marginTop: 4,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 3,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Pagination Skeleton */}
        <div style={{ 
          padding: "12px 20px", 
          borderTop: "1px solid #edf2f7",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div style={{ 
            width: 200, 
            height: 16,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
          <div style={{ display: "flex", gap: "6px" }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{ 
                width: i === 0 || i === 4 ? 70 : 32,
                height: 32,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 6,
                animation: "shimmer 1.5s infinite"
              }}></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        padding: "20px 20px",
        boxSizing: "border-box",
        color: "#1e293b",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* SECTION EN-TÊTE */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1
              style={{
                color: "#294b5a",
                fontSize: "22px",
                fontWeight: 700,
                margin: 0,
                letterSpacing: "-0.02em",
              }}
            >
              Manage Visa Applications
            </h1>
            <span
              style={{
                backgroundColor: "#294b5a",
                color: "#d3be76",
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "12px",
                letterSpacing: "0.5px",
              }}
            >
              System
            </span>
          </div>
          <p
            style={{
              color: "#64748b",
              fontSize: "13.5px",
              margin: 0,
              fontWeight: 400,
            }}
          >
            Overview and management of total{" "}
            <strong style={{ color: "#294b5a" }}>{totalItems}</strong> visa applications.
          </p>
        </div>
      </div>

      {/* BLOC PRINCIPAL CONTENANT FILTRES + TABLE */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
          overflow: "hidden",
          marginTop: "15px",
        }}
      >
        {/* BARRE DE FILTRES */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #edf2f7",
            backgroundColor: "#ffffff",
          }}
        >
          <div className="row g-3 align-items-center">
            {/* Input Recherche */}
            <div className="col-12 col-md-7 col-lg-6">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="Search by reference, name, email or passport..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{
                    width: "100%",
                    padding: "9px 36px 9px 38px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "13px",
                    outline: "none",
                    color: "#0f172a",
                    backgroundColor: "#f8fafc",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#294b5a";
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.boxShadow = "0 0 0 3px rgba(41, 75, 90, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.backgroundColor = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "13px",
                    color: "#94a3b8",
                  }}
                ></i>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#94a3b8",
                      padding: "0",
                    }}
                  >
                    <i className="fa-solid fa-xmark" style={{ fontSize: "14px" }}></i>
                  </button>
                )}
              </div>
            </div>

            {/* Select Status */}
            <div className="col-12 col-md-5 col-lg-4">
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#334155",
                  outline: "none",
                  backgroundColor: "#f8fafc",
                  cursor: "pointer",
                }}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        {isLoading ? (
          <SkeletonLoader />
        ) : (
          <>
            <div className="table-responsive" style={{ margin: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Reference</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Applicant</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Visa Pack</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Passport No</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Date</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Status</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.length > 0 ? (
                    applications.map((item) => {
                      const badgeStyle = getStatusBadgeStyle(item.status);
                      return (
                        <tr
                          key={item.id}
                          style={{ borderBottom: "1px solid #f1f5f9", transition: "background-color 0.15s ease" }}
                          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#fafaf8")}
                          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <td style={{ padding: "14px 16px", fontSize: "13.5px", fontWeight: 700, color: "#294b5a" }}>
                            {item.reference_number}
                          </td>

                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                              {`${item.firstname} ${item.lastname}`}
                            </div>
                            <div style={{ fontSize: "12px", color: "#64748b" }}>{item.email}</div>
                          </td>

                          <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#475569" }}>
                            {item.visa_pack?.name_en || item.visa_pack?.name || "--"}
                          </td>

                          <td style={{ padding: "14px 16px", fontSize: "13px", color: "#475569", fontFamily: "monospace" }}>
                            {item.passport_no}
                          </td>

                          <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#475569" }}>
                            {formatDate(item.created_at)}
                          </td>

                          <td style={{ padding: "14px 16px" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "3px 10px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: 600,
                                backgroundColor: badgeStyle.bg,
                                color: badgeStyle.color,
                                border: `1px solid ${badgeStyle.border}`,
                              }}
                            >
                              {formatStatus(item.status)}
                            </span>
                          </td>

                          <td style={{ padding: "14px 16px", textAlign: "right" }}>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                              <button
                                type="button"
                                onClick={() => navigate(`/admin/details-visa-application/${item.id}`)}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  backgroundColor: "#ffffff",
                                  color: "#294b5a",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = "#f8fafc";
                                  e.currentTarget.style.borderColor = "#cbd5e1";
                                  e.currentTarget.style.color = "#d3be76";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor = "#ffffff";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                  e.currentTarget.style.color = "#294b5a";
                                }}
                                title="Edit Application"
                              >
                                <i className="fa-solid fa-pen-to-square" style={{ fontSize: "13px" }}></i>
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDeleteClick(item)}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  backgroundColor: "#ffffff",
                                  color: "#64748b",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = "#fef2f2";
                                  e.currentTarget.style.borderColor = "#fecaca";
                                  e.currentTarget.style.color = "#dc2626";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor = "#ffffff";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                  e.currentTarget.style.color = "#64748b";
                                }}
                                title="Delete Application"
                              >
                                <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "48px 20px", color: "#64748b", fontSize: "13.5px" }}>
                        <i
                          className="fa-solid fa-passport mb-2"
                          style={{ fontSize: "24px", color: "#cbd5e1", display: "block" }}
                        ></i>
                        {searchTerm || statusFilter !== "all"
                          ? "No visa applications found matching your criteria."
                          : "No visa applications found."}
                        {(searchTerm || statusFilter !== "all") && (
                          <div style={{ marginTop: "8px" }}>
                            <button
                              onClick={clearSearch}
                              style={{
                                background: "none",
                                border: "none",
                                color: "#294b5a",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: 600,
                                textDecoration: "underline",
                              }}
                            >
                              Clear search and filters
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* BARRE DE PAGINATION */}
            {totalItems > 0 && (
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid #edf2f7",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  Showing <strong>{fromItem}</strong> to <strong>{toItem}</strong> of <strong>{totalItems}</strong> entries
                </div>

                {lastPage > 1 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12.5px",
                        fontWeight: "600",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                        color: currentPage === 1 ? "#94a3b8" : "#294b5a",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      Previous
                    </button>

                    {getPaginationRange(currentPage, lastPage).map((item, index) => {
                      if (item === "...") {
                        return (
                          <span
                            key={`dots-${index}`}
                            style={{ padding: "0 6px", fontSize: "13px", color: "#94a3b8", fontWeight: "600", userSelect: "none" }}
                          >
                            ...
                          </span>
                        );
                      }
                      const pageNum = item as number;
                      const isActive = currentPage === pageNum;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          style={{
                            width: "32px",
                            height: "32px",
                            fontSize: "12.5px",
                            fontWeight: "600",
                            borderRadius: "6px",
                            border: isActive ? "none" : "1px solid #cbd5e1",
                            backgroundColor: isActive ? "#294b5a" : "#ffffff",
                            color: isActive ? "#ffffff" : "#334155",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, lastPage))}
                      disabled={currentPage === lastPage}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12.5px",
                        fontWeight: "600",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                        color: currentPage === lastPage ? "#94a3b8" : "#294b5a",
                        cursor: currentPage === lastPage ? "not-allowed" : "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal: Confirmation de suppression */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ padding: "28px 24px 20px 24px", textAlign: "center" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <i className="fa-solid fa-trash-can" style={{ color: "#dc2626", fontSize: "22px" }}></i>
              </div>

              <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#294b5a", margin: "0 0 8px 0", letterSpacing: "-0.01em" }}>
                Delete Visa Application?
              </h2>

              <p style={{ fontSize: "13.5px", color: "#64748b", margin: 0, lineHeight: "1.5" }}>
                Are you sure you want to delete this application? This action cannot be undone.
              </p>

              {selectedApplication && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px 16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    textAlign: "left",
                  }}
                >
                  <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#0f172a" }}>
                    Ref: {selectedApplication.reference_number}
                  </div>
                  <div style={{ fontSize: "12.5px", color: "#64748b" }}>
                    {selectedApplication.firstname} {selectedApplication.lastname} ({selectedApplication.email})
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px 24px 20px 24px", backgroundColor: "#ffffff" }}>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#64748b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.color = "#0f172a";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.color = "#64748b";
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#ffffff",
                  backgroundColor: "#dc2626",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#b91c1c";
                  e.currentTarget.style.boxShadow = "0 4px 10px rgba(220, 38, 38, 0.35)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc2626";
                  e.currentTarget.style.boxShadow = "0 2px 6px rgba(220, 38, 38, 0.25)";
                }}
              >
                <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListRequestVisa;